import {
    Box,
    Button,
    FormField,
    Heading,
    Label,
    Paragraph,
    Select,
} from '@zillow/constellation-10';
import type { ParagraphPropsInterface } from '@zillow/constellation-10';
import { event } from '@zillow/universal-analytics';
import { yieldCallback } from '@zillow/yield-callback';
import React, { useState, useMemo } from 'react';

import type { SystemStyleObject } from '@/styled-system/types';

import type { ResearchDataSection } from './ResearchData.data';

/**
 * Restores the single-line rendering v8 got for free from Chromium's UA
 * `select { white-space: pre }`. Constellation v10's `.c11n { all: unset }`
 * reset wipes that back to `normal`, so the longer Data Type labels (several
 * run well past 70 characters) wrap onto a second line and grow the control
 * from 48px to 72px. With `alignItems: flex-end` on the row that pushes the
 * Data Type label out of line with Geography's on every section, and v10's own
 * Input sizing (Input.md vertical padding, no explicit height) is built for one
 * line. v8 clipped the overflow without an ellipsis, so `nowrap` alone — no
 * `textOverflow` — is the faithful match.
 */
const selectSingleLineStyles: SystemStyleObject = { whiteSpace: 'nowrap' };

/**
 * The section copy is authored HTML, so it goes in via
 * `dangerouslySetInnerHTML` rather than as children. v10 types Paragraph's
 * `children` as required (v8's Text left it optional), so it has to be stated
 * explicitly: `ReactNode` includes `undefined`, and React only rejects
 * `dangerouslySetInnerHTML` when children is non-nullish.
 *
 * Spread rather than written inline: `children={undefined}` as a JSX attribute
 * type-checks, but both lint rules that guard this pattern are purely syntactic
 * and fire regardless of the value — `react/no-children-prop` and
 * `react/no-danger-with-children`, both `error`. Spreading satisfies them.
 *
 * Spread as a *typed* object rather than suppressed with `@ts-expect-error` —
 * that suppression covered every error on the element, so a genuine mistake in
 * the `css` prop or a future breaking change to Paragraph would have been
 * silently swallowed (and `ban-ts-comment` flags it). `Pick` keeps the build
 * honest: if Paragraph's API changes, this still breaks.
 */
const htmlParagraphProps = (
    html: string
): Pick<ParagraphPropsInterface, 'children' | 'dangerouslySetInnerHTML'> => ({
    children: undefined,
    dangerouslySetInnerHTML: { __html: html },
});

/**
 * Props for the ResearchDataSectionComponent
 */
export interface ResearchDataSectionProps {
    /** The section data to display */
    section: ResearchDataSection;
}

/**
 * Renders a single research data section with dropdowns and download link.
 *
 * @component
 */
export function ResearchDataSectionComponent({
    section,
}: ResearchDataSectionProps): JSX.Element {
    const [selectedDataTypeIndex, setSelectedDataTypeIndex] = useState(0);
    const [selectedGeographyIndex, setSelectedGeographyIndex] = useState(0);

    const selectedDataType = useMemo(() => {
        return section.dropdownOptions[selectedDataTypeIndex];
    }, [section.dropdownOptions, selectedDataTypeIndex]);

    const selectedGeography = useMemo(() => {
        return selectedDataType?.geographies[selectedGeographyIndex];
    }, [selectedDataType, selectedGeographyIndex]);

    const downloadUrl = useMemo(() => {
        return selectedGeography?.url || '';
    }, [selectedGeography]);

    const handleDataTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const nextDataTypeIndex = parseInt(e.target.value, 10);
        setSelectedDataTypeIndex(nextDataTypeIndex);

        // Reset here rather than in an effect. If the new data type has fewer
        // geographies, an effect-based reset would leave one committed render
        // where the index is out of range, making downloadUrl empty.
        const nextGeographies =
            section.dropdownOptions[nextDataTypeIndex]?.geographies ?? [];
        if (selectedGeographyIndex >= nextGeographies.length) {
            setSelectedGeographyIndex(0);
        }
    };

    const handleGeographyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedGeographyIndex(parseInt(e.target.value, 10));
    };

    // Generate a unique ID for this section based on title
    const sectionId = useMemo(
        () => section.title.toLowerCase().replace(/\s+/g, '-'),
        [section.title]
    );

    // v8 FormField wired `htmlFor`/`id` by hand. v10 FormField owns that: it
    // publishes `controlId` on its context, Label reads it as `htmlFor` and
    // Select reads it as `id`. The rendered ids are unchanged.
    const dataTypeControlId = `${sectionId}-data-type`;
    const geographyControlId = `${sectionId}-geography`;

    return (
        // v8 `as="section"` → v10 `asChild`, which renders the child element
        // instead of Box's own div while keeping Box's `c11n` class on it.
        <Box asChild>
            <section data-testid={`research-section-${sectionId}`}>
                {/* v8 <Heading level="2" fontType="h4" fontFamily="serif"> →
                    level={2} (v10 types level as a number) + textStyle="heading-sm".
                    v8 h4 is 24px/32px (ThemeSkunkWorks fontSizes.h4 / lineHeights.h4)
                    and v10 heading-sm resolves to font-sizes-600 = 1.5rem /
                    line-heights-800 = 2rem — an exact match, and the mapping
                    v8-tokens.js records for h4.

                    fontFamily is dropped rather than translated: v8 `serif` maps to
                    {fonts.brand}, and the heading-sm textStyle already sets
                    font-family: var(--fonts-heading), which is var(--fonts-brand) in
                    the zillow theme. (It was already redundant in v8 too —
                    ThemeSkunkWorks sets Heading.defaultFontFamily.h4 = 'serif'.)

                    v8 marginBottom="md" = 24px → 'loose' (spacing-600 = 1.5rem). */}
                <Heading
                    level={2}
                    textStyle="heading-sm"
                    css={{ marginBottom: 'loose' }}
                >
                    {section.title}
                </Heading>

                {/* Description content - rendered as Paragraph components */}
                <Box css={{ marginBottom: 'loose' }}>
                    {section.paragraphs.map((paragraph, index) => (
                        // v8 marginBottom="sm" = 16px → 'default' (spacing-400 = 1rem).
                        // The authored HTML uses <b>/<strong>; v8 got `strong, b {
                        // font-weight: 700 }` from Text's semanticChildrenMixin, and
                        // v10 Paragraph applies the equivalent `c11n-semantic` class
                        // itself, so no extra styling is needed here.
                        <Paragraph
                            key={index}
                            css={{ marginBottom: 'default' }}
                            {...htmlParagraphProps(paragraph)}
                        />
                    ))}
                </Box>

                {/* Dropdowns and download button row.
                    v8 gap '$spacing.sm' = 16px → 'default'. v8 `@media $md` is
                    (min-width: 481px) (ThemeSkunkWorks breakpoints.md = 481) → Panda
                    `md` = 30em = 480px, so the row/column switch moves by 1px. */}
                <Box
                    css={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'default',
                        alignItems: 'stretch',
                        md: {
                            flexDirection: 'row',
                            alignItems: 'flex-end',
                        },
                    }}
                >
                    {/* Data Type dropdown */}
                    <Box
                        css={{
                            flex: '1 1 auto',
                            md: {
                                flex: '1 1 0',
                            },
                        }}
                    >
                        <FormField
                            controlId={dataTypeControlId}
                            label={<Label>{section.dataTypeLabel}</Label>}
                            control={
                                <Select
                                    value={selectedDataTypeIndex}
                                    onChange={handleDataTypeChange}
                                    aria-label={`${section.title} - ${section.dataTypeLabel}`}
                                    css={selectSingleLineStyles}
                                >
                                    {section.dropdownOptions.map(
                                        (option, index) => (
                                            <option
                                                key={option.dataType}
                                                value={index}
                                            >
                                                {option.dataType}
                                            </option>
                                        )
                                    )}
                                </Select>
                            }
                        />
                    </Box>

                    {/* Geography dropdown */}
                    <Box
                        css={{
                            flex: '1 1 auto',
                            md: {
                                flex: '1 1 0',
                            },
                        }}
                    >
                        <FormField
                            controlId={geographyControlId}
                            label={<Label>{section.geographyLabel}</Label>}
                            control={
                                <Select
                                    value={selectedGeographyIndex}
                                    onChange={handleGeographyChange}
                                    aria-label={`${section.title} - ${section.geographyLabel}`}
                                    css={selectSingleLineStyles}
                                >
                                    {selectedDataType?.geographies.map(
                                        (geo, index) => (
                                            <option
                                                key={geo.label}
                                                value={index}
                                            >
                                                {geo.label}
                                            </option>
                                        )
                                    )}
                                </Select>
                            }
                        />
                    </Box>

                    {/* Download button.
                        v8 buttonType="primary" → v10 tone="brand" + emphasis="filled".
                        v8 Button.primary in ThemeSkunkWorks is a solid #0041D9 fill with
                        #FFF text, which is the brand/filled pair in v10; both have to be
                        stated because v10 defaults to emphasis="outlined".

                        v8 as="a" → asChild with a real <a>. Anchor-only attributes stay
                        on the <a> so they are not routed through Slot prop merging. */}
                    <Box css={{ flex: '0 0 auto' }}>
                        <Button asChild tone="brand" emphasis="filled">
                            <a
                                href={downloadUrl}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Download ${selectedDataType?.dataType} for ${selectedGeography?.label}`}
                                onClick={yieldCallback(() => {
                                    event({
                                        envelope: {
                                            event_type_id: '2048',
                                            event_type_version_id: '1',
                                            event_template_id: '126',
                                            event_template_version_id: '1',
                                        },
                                        clickstream_trigger: {
                                            trigger_type_nm: 'interaction',
                                            trigger_location_nm: 'cms',
                                            trigger_source_nm:
                                                'cms_research_data',
                                            trigger_object_nm:
                                                'no_trigger_object',
                                        },
                                        semantic: {
                                            semantic_event_nm: 'cms_click',
                                        },
                                        cms_info: {
                                            block_id: sectionId,
                                            block_type_cd:
                                                'research_data_download',
                                            block_displayed_txt: `Download ${selectedDataType?.dataType} - ${selectedGeography?.label}`,
                                            block_target_url: downloadUrl,
                                            block_target_window_txt: '_blank',
                                        },
                                    });
                                })}
                            >
                                Download
                            </a>
                        </Button>
                    </Box>
                </Box>
            </section>
        </Box>
    );
}
